import React, { useEffect, useState, useMemo } from 'react';

import Button from '../components/Button';
import BetaAttributes from '../components/Attributes';
import {
	AttributeRow,
	TemplateField,
	TemplateTextarea,
	TemplateDropdown,
} from '../components/Attributes/Attributes_old';
import {
	FileUpload,
	AssetSelectorBeta,
	StackableModal,
} from '../components/nextgen';

import useModal from '../hooks/useModal';
import useAsync from '../hooks/useAsync';
import useRequired from '../hooks/useRequired';

import { addTicket } from '../api';
import { getModules } from '../api/modules';
import { getAssets } from '../api/assets';

const MODAL_NAME = 'createTicket';

const CreateTicketModal = () => {
	const [form, setForm] = useState({});
	const [fileList, setFileList] = useState([]);
	const [newAssetIds, setNewAssetIds] = useState([]);

	const { open: options, setClose } = useModal(MODAL_NAME);

	const { execute: onAddTicket, status: isAddingTicket } = useAsync(addTicket, {
		immediate: false,
	});

	const { value: modules, execute: onRefreshModules } = useAsync(getModules, {
		immediate: false,
	});

	const { value: assetsData, execute: onRefreshAssets } = useAsync(getAssets, {
		immediate: false,
	});
	// Find tickets module for category selection
	const ticketsModule = modules?.rows?.find((m) => m.key === 'default:ticket');

	// Process assets data
	const assets = useMemo(() => {
		if (!assetsData?.rows) return [];
		return assetsData.rows.map((asset) => ({
			id: asset.id,
			name:
				[
					asset.manufacturer?.name || asset.manufacturerName,
					asset.model?.name || asset.modelName,
				]
					.filter(Boolean)
					.join(' ') || 'Unnamed Asset',
			manufacturer:
				asset.manufacturer?.name || asset.manufacturerName || 'Unknown',
			model: asset.model?.name || asset.modelName || '',
			type: asset.model?.name || asset.modelName || 'Unknown Type',
			location: asset.location?.name || asset.locationName || 'No Location',
			serial: asset.serialNumber || 'N/A',
		}));
	}, [assetsData]);

	useEffect(() => {
		if (options) {
			onRefreshModules();
			onRefreshAssets({ pageSize: null });
			setForm({
				severity: 'minor',
			});
			setFileList([]);
			// Pre-populate asset if assetId is passed
			setNewAssetIds(options.assetId ? [options.assetId] : []);
		} else {
			setForm({});
			setFileList([]);
			setNewAssetIds([]);
		}
	}, [options]);

	const setItem = (key, value) => {
		setForm((prevForm) => ({ ...prevForm, [key]: value }));
	};

	const configurePayload = () => {
		// Separate uploaded files from library documents
		const uploadedFiles = fileList
			.filter((f) => !f.fromLibrary && f.original)
			.map((f) => f.original);
		const libraryDocIds = fileList
			.filter((f) => f.fromLibrary && f.id)
			.map((f) => f.id);

		return {
			information: {
				type: 'general',
				...(form?.category?.value ? { categoryId: form.category.value } : {}),
				...(form?.subject ? { summary: form.subject } : {}),
				...(form?.description ? { description: form.description } : {}),
				...(form?.severity ? { severity: form.severity } : {}),
				...(form?.location?.value ? { location: form.location.value } : {}),
				// Use first selected asset if any (tickets support single asset)
				...((Array.isArray(newAssetIds) ? newAssetIds.length > 0 : newAssetIds)
					? {
							assetId: Array.isArray(newAssetIds)
								? newAssetIds[0]
								: newAssetIds,
						}
					: {}),
			},
			...(uploadedFiles.length > 0 && { files: uploadedFiles }),
			...(libraryDocIds.length > 0 && { documentIds: libraryDocIds }),
			bucket: 'tickets',
		};
	};

	const hasAllRequiredValues = useRequired(form, ['subject']);

	const handleSubmit = () => {
		const payload = configurePayload();
		onAddTicket(payload, () => {
			if (typeof options?.onSuccess === 'function') {
				options.onSuccess();
			}
			setClose();
		});
	};

	return (
		<StackableModal
			title="Create Ticket"
			open={!!options}
			onClose={() => setClose()}
			size="xl"
		>
			<div className="space-y-5 text-left mt-4">
				{/* Subject */}
				<TemplateField
					name="Title"
					placeholder="Brief summary of the issue"
					dataIndex="subject"
					value={form?.subject}
					onChange={(key, value) => setItem(key, value)}
					required
					maxLength={100}
					showMaxLength
				/>

				{/* Description */}
				<TemplateTextarea
					name="Description"
					placeholder="Whats's broken?  Where is it?  Any details that help..."
					dataIndex="description"
					onChange={(key, value) => setItem(key, value)}
					value={form?.description}
					className="-mt-5"
				/>

				{/* Severity */}
				<TemplateDropdown
					name="Priority"
					options={[
						{ label: 'Critical', value: 'critical' },
						{ label: 'Major', value: 'major' },
						{ label: 'Minor', value: 'minor' },
					]}
					placeholder="Select severity level"
					dataIndex="severity"
					onChange={(key, value) => setItem(key, value)}
					value={form?.severity}
				/>

				{/* Category Selection */}
				<div>
					<BetaAttributes
						field={{
							label: 'Category',
							type: 'category',
							key: 'category',
							moduleId: ticketsModule?.id,
							options: { module_key: 'default:ticket' },
						}}
						form={form}
						onChange={(index, value) => {
							setForm((prevForm) => ({ ...prevForm, category: value }));
						}}
					/>
				</div>

				{/* Related Asset */}
				<AttributeRow name="Related Asset">
					<AssetSelectorBeta
						value={newAssetIds}
						onChange={setNewAssetIds}
						assets={assets}
						placeholder="Select assets by name, serial number, location, etc…"
					/>
				</AttributeRow>

				{/* Location Selection */}
				<div>
					<BetaAttributes
						field={{
							label: 'Location',
							type: 'location',
							key: 'location',
						}}
						form={form}
						onChange={(index, value, _, rawLocation) => {
							setForm({
								...form,
								location: { value, label: rawLocation?.name },
							});
						}}
					/>
				</div>

				{/* Document Upload */}
				<div>
					<label className="block text-sm font-medium text-gray-700 mb-2">
						Attachments
					</label>
					<FileUpload
						files={fileList}
						onChange={setFileList}
						multiple
						acceptTypes="image/jpeg image/png application/pdf"
						showLibraryOption
						buttonText="Add Attachments"
					/>
				</div>
			</div>

			{/* Footer */}
			<div className="flex justify-end mt-6 pt-4">
				<Button type="secondary" onClick={() => setClose()} className="mr-3">
					Cancel
				</Button>
				<Button
					onClick={handleSubmit}
					disabled={!hasAllRequiredValues || isAddingTicket === 'pending'}
				>
					{isAddingTicket === 'pending'
						? fileList?.length
							? 'Uploading...'
							: 'Creating...'
						: 'Create Ticket'}
				</Button>
			</div>
		</StackableModal>
	);
};

export default CreateTicketModal;
